import { emitUpgradeRequired } from "./upgradeBus.ts"
import { emitSessionExpired } from "./sessionBus.ts"

// Once a 401 has scheduled the re-login redirect, ignore further 401s so
// concurrent failing requests don't each queue their own navigation.
let reloginScheduled = false

export async function handleResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
        // 401 — token invalid/expired/revoked: tell the user their session
        // ended, then send them to re-login (briefly delayed so the notice is
        // visible rather than a silent jump to the login page).
        if (res.status === 401) {
            const result = await res.json().catch(() => null)
            if (result?.redirect && !reloginScheduled) {
                reloginScheduled = true
                emitSessionExpired()
                setTimeout(() => window.location.replace(result.redirect), 1500)
            }
            throw new Error('401: ' + (result?.error ?? 'Unauthorized'))
        }

        // 402/403 — no subscription / not permitted: show the upgrade plate
        // (or honour a legacy redirect, e.g. the terms-of-use gate).
        if (res.status === 402 || res.status === 403) {
            const result = await res.json().catch(() => null)
            if (result?.code === 'upgrade-required' && result?.upgradeUrl) {
                emitUpgradeRequired(result.upgradeUrl)
            } else if (result?.redirect) {
                window.location.replace(result.redirect)
            }
            throw new Error(res.status + ': ' + (result?.code ?? 'Forbidden'))
        }

        let errorText: string

        const contentType = res.headers.get('content-type')

        if (contentType && contentType.includes('application/json')) {
            const body = await res.json()
            errorText = typeof body === 'string' ? body :
                body.errors.map((error: { msg: string }) => error.msg).join('\n')
        } else {
            errorText = await res.text()
        }

        throw new Error(res.status + ': ' + errorText)
    }

    let json
    try {
        json = await res.json()
    } catch (e) {
        if (e instanceof SyntaxError) {
            throw new Error(e.message)
        }
    }

    return json.data ? json.data : json
}

export const getErrorCode = (error: Error) =>
    error.message.substring(0, error.message.indexOf(":"))
